import { createColumnHelper } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { LocationType } from '@invenco/accounts-interface';
import type { LocationDTO } from '@invenco/accounts-interface';
import { ShippingMethodType } from '@invenco/common-domain/enums/ShippingMethod';
import type { ShippingMethodDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import type { RowClickHandler } from '@/components/data-table/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { shippingMethodStatusType } from '@/shared/status-maps';
import { shippingMethodAutoInviteTitle } from '@/shared/title-maps';

import { ManagedShippingMethodDialogContent } from './managed-shipping-method-dialog-content';
import type { ManagedShippingMethodEditFormValues } from './managed-shipping-method-edit-dialog-content';
import { ManagedShippingMethodEditDialogContent } from './managed-shipping-method-edit-dialog-content';
import type { WarehouseShippingMethodFormValues } from './warehouse-shipping-method-dialog-content';
import { WarehouseShippingMethodDialogContent } from './warehouse-shipping-method-dialog-content';

type Props = {
  shippingMethods: ShippingMethodDTO[];
  isLoading?: boolean;
  locationId?: string;
  accountId?: string;
  warehouseLocationId?: string | null;
  locationType?: LocationDTO['type'];
};

const columnHelper = createColumnHelper<ShippingMethodDTO>();

const baseColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('carrierName', {
    header: 'Carrier',
  }),
  columnHelper.accessor('carrierServiceName', {
    header: 'Service',
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => <StatusBadge status={getValue()} type={shippingMethodStatusType} />,
  }),
  columnHelper.accessor('ratesCarrierAccountName', {
    header: 'Rates carrier account',
  }),
  columnHelper.accessor('bookingCarrierAccountName', {
    header: 'Booking carrier account',
  }),
  columnHelper.accessor('dynamicallyPriced', {
    header: 'Dynamically priced',
    cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
  }),
];

const autoInviteColumn = columnHelper.accessor('autoInvite', {
  header: 'Auto-invite',
  cell: ({ getValue }) => shippingMethodAutoInviteTitle[getValue()],
});

const SUPPORTED_LOCATION_TYPES: LocationDTO['type'][] = [
  LocationType.WAREHOUSE,
  LocationType.MANAGED,
];

export function ShippingMethodsCard({
  shippingMethods,
  isLoading,
  locationId,
  accountId,
  warehouseLocationId,
  locationType,
}: Props) {
  const { shippingGateway } = useGateways();

  // Drives the warehouse edit side panel. Set on row click, cleared on close so reopening
  // with a different row reads fresh state rather than the previously edited DTO.
  const [editTarget, setEditTarget] = useState<ShippingMethodDTO | null>(null);

  // Separate target for the managed edit panel. The card only ever renders one location type,
  // so warehouse and managed edit state never collide; keeping them apart keeps each panel's
  // wiring self-contained.
  const [managedEditTarget, setManagedEditTarget] = useState<ShippingMethodDTO | null>(null);

  const columns = useMemo(
    () =>
      locationType === LocationType.WAREHOUSE ? [...baseColumns, autoInviteColumn] : baseColumns,
    [locationType],
  );
  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'location-shipping-methods',
  });

  const table = useDataTable({
    data: shippingMethods,
    columns,
    state,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    onSortingChange,
  });

  const { mutate: addWarehouseShippingMethod } = useGatewayMutation({
    // skipManagedCascade rides on the form values for type simplicity, but it is a
    // warehouse-PATCH-only flag (create has no cascade). Peel it off via the rest destructure so
    // it never leaks into the create request body through the spread below.
    mutationFn: ({ skipManagedCascade, ...shippingMethod }: WarehouseShippingMethodFormValues) => {
      if (!accountId || !locationId) {
        throw new Error('Location account details not available');
      }

      return shippingGateway.createShippingMethod({
        ...shippingMethod,
        accountId,
        locationId,
        type: ShippingMethodType.WAREHOUSE,
      });
    },
    linkedQuery: ['shippingMethods', { locationId }],
    linkResponseToQuery: false,
    successMessage: 'Warehouse shipping method added',
  });

  const { mutate: updateWarehouseShippingMethod } = useGatewayMutation({
    mutationFn: (shippingMethod: WarehouseShippingMethodFormValues) => {
      if (!editTarget) {
        throw new Error('No shipping method selected to edit');
      }

      // The dialog hands back every form value, but the backend UpdateShippingMethod use case
      // only accepts these fields — carrierServiceId and name are locked in the form and
      // intentionally not sent.
      return shippingGateway.updateShippingMethod(
        editTarget.id,
        {
          status: shippingMethod.status,
          ratesCarrierAccountId: shippingMethod.ratesCarrierAccountId,
          bookingCarrierAccountId: shippingMethod.bookingCarrierAccountId,
          autoInvite: shippingMethod.autoInvite,
          dynamicallyPriced: shippingMethod.dynamicallyPriced,
        },
        // `skipManagedCascade` is `skip_managed_cascade` request metadata (ENG-5316), sent as a
        // body-root sibling of the method rather than a field on it. When set, the update touches
        // only the warehouse method and leaves managed children untouched.
        { skipManagedCascade: shippingMethod.skipManagedCascade },
      );
    },
    // Same key as the add mutation so the table refreshes through the same linked query.
    linkedQuery: ['shippingMethods', { locationId }],
    linkResponseToQuery: false,
    successMessage: 'Warehouse shipping method updated',
  });

  const { mutate: addManagedShippingMethod } = useGatewayMutation({
    mutationFn: (warehouseShippingMethod: ShippingMethodDTO) => {
      if (!accountId || !warehouseLocationId || !locationId) {
        throw new Error('Managed location details not available');
      }

      if (warehouseShippingMethod.locationId !== warehouseLocationId) {
        throw new Error('Invalid warehouse shipping method provided, location ids do not match.');
      }

      return shippingGateway.createShippingMethod({
        name: warehouseShippingMethod.name,
        accountId,
        locationId,
        warehouseShippingMethodId: warehouseShippingMethod.id,
        carrierServiceId: warehouseShippingMethod.carrierServiceId,
        bookingCarrierAccountId: warehouseShippingMethod.bookingCarrierAccountId,
        ratesCarrierAccountId: warehouseShippingMethod.ratesCarrierAccountId,
      });
    },
    linkedQuery: ['shippingMethods', { locationId }],
    linkResponseToQuery: false,
    successMessage: 'Managed shipping method added',
  });

  const { mutate: updateManagedShippingMethod } = useGatewayMutation({
    mutationFn: (values: ManagedShippingMethodEditFormValues) => {
      if (!managedEditTarget) {
        throw new Error('No shipping method selected to edit');
      }

      // Carrier service and name are inherited from the parent warehouse method and not editable
      // on a managed method, so only the four edit-form fields are sent.
      return shippingGateway.updateShippingMethod(managedEditTarget.id, {
        status: values.status,
        ratesCarrierAccountId: values.ratesCarrierAccountId,
        bookingCarrierAccountId: values.bookingCarrierAccountId,
        dynamicallyPriced: values.dynamicallyPriced,
      });
    },
    // Same key as the add mutation so the table refreshes through the same linked query.
    linkedQuery: ['shippingMethods', { locationId }],
    linkResponseToQuery: false,
    successMessage: 'Managed shipping method updated',
  });

  const isWarehouse = locationType === LocationType.WAREHOUSE;
  const isManaged = locationType === LocationType.MANAGED;

  // Row click opens the edit side panel for the matching location type. Editing requires
  // an accountId for the carrier-account dropdowns, so rows are inert without it.
  const onWarehouseRowClick = useCallback<RowClickHandler<ShippingMethodDTO>>(
    (row) => setEditTarget(row.original),
    [],
  );

  const onManagedRowClick = useCallback<RowClickHandler<ShippingMethodDTO>>(
    (row) => setManagedEditTarget(row.original),
    [],
  );

  const rowClickHandler = accountId
    ? isWarehouse
      ? onWarehouseRowClick
      : isManaged
        ? onManagedRowClick
        : undefined
    : undefined;

  const canAddShippingMethod =
    Boolean(locationId) &&
    locationType !== undefined &&
    SUPPORTED_LOCATION_TYPES.includes(locationType);

  return (
    <Card aria-label="Shipping methods">
      <CardHeader>
        <DataTableCardTitle table={table}>Shipping methods</DataTableCardTitle>
        {canAddShippingMethod ? (
          <CardAction>
            <FormDialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="ghost">
                  <Plus />
                  New shipping method
                </Button>
              </DialogTrigger>
              {locationType === LocationType.WAREHOUSE && accountId ? (
                <WarehouseShippingMethodDialogContent
                  accountId={accountId}
                  mode="add"
                  existingShippingMethods={shippingMethods}
                  onSubmit={addWarehouseShippingMethod}
                />
              ) : null}
              {locationType === LocationType.MANAGED && warehouseLocationId ? (
                <ManagedShippingMethodDialogContent
                  warehouseLocationId={warehouseLocationId}
                  existingShippingMethods={shippingMethods}
                  onSubmit={addManagedShippingMethod}
                />
              ) : null}
            </FormDialog>
          </CardAction>
        ) : null}
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} onClickRow={rowClickHandler} />
      </DataTableCardBody>
      {isWarehouse && accountId ? (
        <FormDialog
          open={editTarget !== null}
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
        >
          <WarehouseShippingMethodDialogContent
            accountId={accountId}
            mode="edit"
            shippingMethod={editTarget ?? undefined}
            existingShippingMethods={shippingMethods}
            onSubmit={updateWarehouseShippingMethod}
          />
        </FormDialog>
      ) : null}
      {isManaged && accountId ? (
        <FormDialog
          open={managedEditTarget !== null}
          onOpenChange={(open) => {
            if (!open) setManagedEditTarget(null);
          }}
        >
          <ManagedShippingMethodEditDialogContent
            accountId={accountId}
            shippingMethod={managedEditTarget ?? undefined}
            onSubmit={updateManagedShippingMethod}
          />
        </FormDialog>
      ) : null}
    </Card>
  );
}
